<template>
  <div v-if="!loading">
    <v-alert
      dense
      text
      color="info"
      icon="mdi-information-outline"
      class="ma-4"
    >
      <div class="text-caption">
        <strong>Priebeh hodnotenia:</strong> Grafické zobrazenie vývoja CRA skóre v čase<br>
        <strong>Probačný a ochrannný dohľad:</strong> Textový popis dohľadu nad klientom
      </div>
    </v-alert>
    <form>
      <v-row>
        <v-col>
          <v-card>
            <v-card-title>
              Priebeh hodnotení
            </v-card-title>
            <v-card-text>
              <div v-if="form.cra_graph.length > 0">
                <apexchart
                  type="line"
                  height="350"
                  :options="chartOptions"
                  :series="graphData"
                />
              </div>
              <div v-else>
                Hodnotenia nie sú k dispozícií
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>Probačný a ochranný dohľad</v-card-title>
            <v-card-text>
              <v-textarea
                v-model="form.probacny_a_ochranny_dohlad_popis"
                auto-grow
                rows="1"
                name="Textový popis"
                label="Textový popis"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <!-- Form Actions with unsaved changes tracking -->
      <v-divider class="my-6" />
      <FormActions
        v-if="!form.closed"
        ref="formActions"
        :fixed="false"
        :is-dirty="isDirty"
        :loading="loading"
        save-text="Uložiť"
        @save="updateItem"
      />
    </form>
  </div>

  <div v-else>
    <form-skeleton-component skeleton_type="RizikaADohlad" />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from '@vue/composition-api';
import { useRoute } from '@/composables/useRouter.js';
import { APIService } from "@/http/APIService.js";
import VueApexCharts from 'vue-apexcharts';
import FormSkeleton from "../components/FormSkeleton.vue";
import FormActions from "@/components/FormActions.vue";
import { useIapszForm } from "@/composables/useIapszForm.js";
import { useFormDirty } from "@/composables/useFormDirty.js";

const apiService = new APIService();

export default {
  components: {
    "form-skeleton-component": FormSkeleton,
    FormActions,
    apexchart: VueApexCharts,
  },

  setup() {
    const route = useRoute();
    
    // Use composables
    const { loading, setLoading, handleSaveSuccess, handleSaveError } = useIapszForm();
    const formActions = ref(null);
    
    // Form state
    const form = ref({
      id: null,
      probacny_a_ochranny_dohlad_popis: "",
      cra_graph: [],
      closed: false,
    });
    
    // Track dirty state
    const initialForm = ref({});
    const { isDirty, markAsPristine, enableBrowserWarning } = useFormDirty(initialForm, form, { deep: true });
    enableBrowserWarning();

    const selects = reactive({

      nepravdive_zavadzajuce: [
        "podľa výsledkov náhodného overenia vybraných informácii poskytovaných odsúdeným odsúdený UVIEDOL nepravdivé/zavádzajúce informácie pri hodnotení, vo vstupnom dotazníku a pri pohovoroch",
        "podľa výsledkov náhodného overenia vybraných informácii poskytovaných odsúdeným odsúdený NEUVIEDOL nepravdivé/zavádzajúce informácie pri hodnotení, vo vstupnom dotazníku a pri pohovoroch",
      ],
    });

    const manual = reactive({
      nepravdive_zavadzajuce: {
        nazov: "Nepravdivé/zavádzajúce informácie",
        priklad: [],
        poznamka: [],
        zdroj: [],
        charakteristika: [
          "Ak ktorýkoľvek z hodnotiteľov zistí akýkoľvek rozpor (napr. klamstvo, zavádzanie...) v údajoch poskytnutých odsúdeným,  túto skutočnosť oznámi prípadovému manažérovi.",
        ],
      },
    });

    // Computed properties
    const graphData = computed(() => {
      const series = form.value.cra_graph.map(function (element) {
        return [new Date(element.created_at).getTime(), element.score];
      });
      return [{ name: "CRA", data: series }];
    });

    const chartOptions = computed(() => {
      return {
        chart: {
          height: 200,
          type: 'area',
          zoom: {
            enabled: false
          }
        },
        dataLabels: {
          enabled: true,
        },
        stroke: {
          curve: 'straight'
        },
        grid: {
          row: {
            colors: ['#f3f3f3', 'transparent'],
            opacity: 0.5
          },
        },
        xaxis: {
          type: "datetime",
          labels: {
            format: "dd/MM/yy"
          }
        },
        yaxis: {
          min: 0,
          max: 115
        },

        tooltip: {
          custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            const graphData = form.value.cra_graph[dataPointIndex];
            return '<div class="ma-2"><div>' +
              '<span>' + 'Typ: CRA-' + graphData.type + '</span>' +
              '</div>' +
              '<div>' +
              '<span>' + 'Verzia: ' + graphData.version + '</span>' +
              '</div>' +
              '<div>' +
              '<span>' + 'Percento: ' + graphData.percentage + '% </span>' +
              '</div>' +
              '<div>' +
              '<span>' + 'Riziko: ' + graphData.string_score + '</span>' +
              '</div>' +
              '<div>' +
              '<span>' + 'Dôvod: ' + graphData.comment + '</span>' +
              '</div>' +
              '<div>' +
              '<span>' + 'Ústav: ' + graphData.institution_full.name + '</span>' +
              '</div></div>'
          }
        },

        annotations: {
          yaxis: [
            {
              y: 0,
              y2: 25,
              borderColor: 'green',
              fillColor: 'green',
              opacity: 0.1,
              label: {
                borderColor: '#333',
                style: {
                  fontSize: '10px',
                  color: 'white',
                  background: 'green',
                },
                text: 'Nízke riziko',
              }
            },

            {
              y: 25,
              y2: 49,
              borderColor: 'orange',
              fillColor: 'orange',
              opacity: 0.1,
              label: {
                borderColor: '#333',
                style: {
                  fontSize: '10px',
                  color: 'white',
                  background: 'orange',
                },
                text: 'Stredné riziko',
              }
            },

            {
              y: 49,
              y2: 115,
              borderColor: 'red',
              fillColor: 'red',
              opacity: 0.1,
              label: {
                textAnchor: "left",
                borderColor: '#333',
                position: "left",
                style: {
                  fontSize: '10px',
                  color: 'white',
                  background: 'red',
                },
                text: 'Vysoké riziko',
              }
            },
          ],

          xaxis: [{
            x: new Date('01 Oct 2019').getTime(),
            borderColor: '#999',
            yAxisIndex: 0,
            label: {
              show: true,
              text: 'Začiatok pilotnej prevádzky',
              style: {
                color: "#fff",
                background: 'orange'
              }
            }
          },
          {
            x: new Date('31 Jan 2020').getTime(),
            borderColor: '#999',
            yAxisIndex: 0,
            label: {
              show: true,
              text: 'Koniec pilotnej prevádzky',
              style: {
                color: "#fff",
                background: "orange"
              }
            }
          },
          {
            x: new Date('01 Jan 2021').getTime(),
            borderColor: '#999',
            yAxisIndex: 0,
            label: {
              show: true,
              text: 'Začiatok ostrej prevádzky',
              style: {
                color: "#fff",
                background: 'green'
              }
            }
          }]
        }

      };
    });

    // Methods
    const getItem = () => {
      setLoading(true);
      apiService
        .getIapszRizikaADohlad(route.params.id)
        .then((response) => {
          form.value = response;
          initialForm.value = JSON.parse(JSON.stringify(response));
          markAsPristine();
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    };

    const updateItem = () => {
      apiService
        .updateIapszRizikaADohlad(form.value, route.params.id)
        .then(
          (response) => {
            handleSaveSuccess();
            if (formActions.value) {
              formActions.value.showSuccess();
            }
            form.value = response;
            markAsPristine();
          },
          () => {
            handleSaveError();
            if (formActions.value) {
              formActions.value.showError();
            }
          }
        );
    };

    // Lifecycle
    onMounted(() => {
      getItem();
    });

    return {
      // State
      loading,
      form,
      selects,
      manual,
      isDirty,
      formActions,
      
      // Computed
      graphData,
      chartOptions,
      
      // Methods
      updateItem,
    };
  },

  beforeRouteLeave(to, from, next) {
    if (this.isDirty) {
      if (window.confirm('Máte neuložené zmeny. Naozaj chcete odísť?')) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
};
</script>